/**
 * AthleteHome — slim dashboard rendered when a user is in Athlete Mode
 * (parent-controlled child sign-in). Shows ONLY what the parent has permitted
 * via permission flags on the user record:
 *   - workouts          → "My Workouts" tile + open the existing runner
 *   - sessions          → sessions remaining + upcoming session list
 *   - maxes             → inline list of current rep maxes
 *   - book_own_sessions → adds a "Book a slot" link to the Sessions tile
 *
 * Anything not listed above is hidden — billing, family info, waivers,
 * emergency contact, lead/CRM data. The athlete can NEVER see another
 * sibling or the parent's pricing.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import LatestSessionLiteCard from "@/components/LatestSessionLiteCard";
import {
    Dumbbell,
    Calendar,
    Activity,
    Loader2,
    LogOut,
    UserCircle2,
    ArrowRight,
    Flame,
    AlertOctagon,
    MessageSquare,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function formatDate(iso) {
    if (!iso) return "";
    const d = new Date(`${iso}T00:00:00`);
    return `${WEEKDAY_LABELS[d.getDay()]}, ${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
}

export default function AthleteHome() {
    const { user, logout, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [ctx, setCtx] = useState(null);
    const [strikes, setStrikes] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!authLoading && !user) navigate("/", { replace: true });
        // If a non-athlete-mode user somehow lands here, bounce to dashboard.
        if (!authLoading && user && !user.athlete_mode_for) {
            navigate("/dashboard", { replace: true });
        }
    }, [authLoading, user, navigate]);

    useEffect(() => {
        if (!user?.athlete_mode_for) return;
        (async () => {
            try {
                const [r, s] = await Promise.all([
                    axios.get(`${API}/me/athlete-context`, { withCredentials: true }),
                    axios.get(`${API}/me/strikes`, { withCredentials: true }).catch(() => ({ data: null })),
                ]);
                setCtx(r.data);
                setStrikes(s.data);
            } catch (e) {
                setError(e?.response?.data?.detail || "Couldn't load your dashboard");
            } finally {
                setLoading(false);
            }
        })();
    }, [user]);

    if (authLoading || loading) {
        return (
            <div className="min-h-screen bg-black text-white flex items-center justify-center">
                <Loader2 className="w-7 h-7 animate-spin text-brand-red" />
            </div>
        );
    }
    if (!user || !ctx) {
        return (
            <div className="min-h-screen bg-black text-white flex items-center justify-center">
                <p data-testid="athlete-home-error" className="text-sm text-zinc-300">{error || "Loading..."}</p>
            </div>
        );
    }

    const perms = ctx.permissions || {};
    const firstName = (ctx.first_name || user.name || "Athlete").split(" ")[0];

    const handleLogout = async () => {
        await logout();
        navigate("/", { replace: true });
    };

    return (
        <div data-testid="athlete-home" className="min-h-screen bg-brand-obsidian text-white pb-20 md:pb-0">
            <section className="relative pt-32 pb-12 border-b border-zinc-900">
                <div className="absolute inset-0 bg-grid-faint opacity-30 pointer-events-none" />
                <div className="relative max-w-5xl mx-auto px-6 md:px-10">
                    <div className="flex items-center justify-between gap-4">
                        <div>
                            <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-3 inline-flex items-center gap-3">
                                <span className="inline-block w-10 h-px bg-brand-red" />
                                Athlete Mode
                            </div>
                            <h1
                                data-testid="athlete-welcome"
                                className="font-display text-5xl md:text-6xl tracking-wider leading-[0.9] text-white"
                            >
                                HEY, <span className="text-brand-red">{firstName.toUpperCase()}.</span>
                            </h1>
                            <p className="mt-4 text-zinc-300 text-sm md:text-base max-w-xl">
                                Your gym, your stuff. Train hard, log honest.
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                data-testid="athlete-messages-btn"
                                onClick={() => navigate("/dashboard/messages")}
                                className="btn-ghost-tactical inline-flex text-xs"
                            >
                                <MessageSquare className="w-3.5 h-3.5" /> Messages
                            </button>
                            <button
                                type="button"
                                data-testid="athlete-signout"
                                onClick={handleLogout}
                                className="btn-ghost-tactical inline-flex text-xs"
                            >
                                <LogOut className="w-3.5 h-3.5" /> Sign out
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-12">
                <div className="max-w-5xl mx-auto px-6 md:px-10">
                    {perms.sessions && (
                        <AthleteSummaryStrip
                            sessionsRemaining={ctx.sessions_remaining}
                            upcoming={ctx.upcoming_bookings || []}
                            onScheduleClick={() => navigate("/athlete/schedule")}
                        />
                    )}
                    {perms.sessions && (typeof ctx.streak_weeks === "number" || typeof ctx.sessions_last_30d === "number") && (
                        <StreakStrip
                            streak={ctx.streak_weeks || 0}
                            last30={ctx.sessions_last_30d || 0}
                            firstName={firstName}
                            daysStreak={ctx.streak_days || 0}
                            longestStreak={ctx.longest_streak_weeks || 0}
                        />
                    )}
                    {perms.sessions && strikes && ((strikes.late_cancels || 0) + (strikes.no_shows || 0) > 0) && (
                        <div
                            data-testid="athlete-strikes-strip"
                            className="mb-6 border border-brand-red/40 bg-brand-red/10 rounded-2xl px-5 py-3 flex items-center gap-3"
                        >
                            <AlertOctagon className="w-5 h-5 text-brand-red shrink-0" />
                            <div className="text-sm text-zinc-200 flex-1 min-w-0">
                                <span className="text-white font-semibold">This month:</span>{" "}
                                <span data-testid="athlete-strikes-late" className="text-brand-red font-display tracking-wider">{strikes.late_cancels}</span> late cancel{strikes.late_cancels === 1 ? "" : "s"}
                                {" · "}
                                <span data-testid="athlete-strikes-noshow" className="text-brand-red font-display tracking-wider">{strikes.no_shows}</span> no-show{strikes.no_shows === 1 ? "" : "s"}
                                <span className="block text-[11px] text-zinc-400 mt-0.5">Be on time. Strikes can cost a session.</span>
                            </div>
                        </div>
                    )}
                    {perms.sessions && (
                        <div className="mb-6">
                            <LatestSessionLiteCard />
                        </div>
                    )}
                    <div className="grid sm:grid-cols-2 gap-4">
                    {perms.workouts && (
                        <ActionCard
                            testid="athlete-card-workouts"
                            onClick={() => navigate("/dashboard/workouts")}
                            icon={Dumbbell}
                            title="My workouts"
                            sub="Tap to open your programming — view it anytime."
                            accent="red"
                        />
                    )}
                    {perms.sessions && (
                        <SessionsCard
                            sessionsRemaining={ctx.sessions_remaining}
                            upcoming={ctx.upcoming_bookings || []}
                            canBook={!!perms.book_own_sessions}
                            onOpen={() => navigate("/athlete/schedule")}
                        />
                    )}
                    {perms.maxes && (
                        <MaxesCard maxes={ctx.maxes || {}} onOpen={() => navigate("/dashboard/workouts")} />
                    )}
                </div>

                {Object.values(perms).every((v) => !v) && (
                    <div data-testid="athlete-no-perms" className="mt-8 max-w-2xl mx-auto border border-zinc-800 rounded-2xl bg-zinc-950/50 p-8 text-center text-sm text-zinc-300">
                        <UserCircle2 className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                        Your account is set up, but no sections are turned on yet. Ask your parent to enable Workouts, Sessions, or Maxes.
                    </div>
                )}

                <p className="mt-10 text-center text-[11px] text-zinc-500">
                    Billing, plans, and family info live on your parent&apos;s account.
                </p>
                </div>
            </section>
            <AthleteBottomNav perms={perms} />
        </div>
    );
}


/**
 * Sticky bottom tab bar for athlete-mode users so they can always reach
 * Workouts and Schedule from any screen. Sachin 2026-02-23: "athletes
 * can't find their workouts — make sure there's a button at the bottom."
 */
function AthleteBottomNav({ perms }) {
    const navigate = useNavigate();
    const tabs = [
        { key: "home", icon: Activity, label: "Home", to: "/athlete" },
    ];
    if (perms?.workouts) {
        // Sachin 2026-02-28: athlete-mode kids ALWAYS see workouts. The
        // bottom-nav tab no longer disables on "not checked in" — the
        // gym-check-in gate is gone for content browsing.
        tabs.push({
            key: "workouts",
            icon: Dumbbell,
            label: "Workouts",
            to: "/dashboard/workouts",
        });
    }
    if (perms?.sessions) {
        tabs.push({ key: "schedule", icon: Calendar, label: "Schedule", to: "/athlete/schedule" });
    }
    // 2026-07 (Sachin): kids can message the gym too.
    tabs.push({ key: "messages", icon: MessageSquare, label: "Messages", to: "/dashboard/messages" });
    if (tabs.length < 2) return null;
    return (
        <nav
            data-testid="athlete-bottom-nav"
            className="md:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-zinc-900 bg-black/95 backdrop-blur pb-[env(safe-area-inset-bottom)]"
        >
            <div className={`grid grid-cols-${tabs.length}`} style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}>
                {tabs.map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        data-testid={`athlete-bottom-${t.key}`}
                        onClick={() => {
                            if (t.disabled) { toast.info(t.disabledMsg); return; }
                            navigate(t.to);
                        }}
                        className={`flex flex-col items-center justify-center gap-1 py-2.5 text-[10px] tracking-[0.2em] uppercase transition-colors ${
                            t.disabled ? "text-zinc-600" : "text-zinc-400 hover:text-white active:text-brand-red"
                        }`}
                    >
                        <t.icon className="w-5 h-5" />
                        <span>{t.label}</span>
                    </button>
                ))}
            </div>
        </nav>
    );
}

function StreakStrip({ streak, last30, firstName, daysStreak = 0, longestStreak = 0 }) {
    const headline =
        streak >= 4
            ? `${streak} weeks in a row. Don't break it, ${firstName}.`
            : streak >= 2
                ? `${streak} weeks in a row — keep stacking.`
                : streak === 1
                    ? "On the board this week."
                    : "No sessions yet this week.";
    const showRecord = longestStreak >= 4 && longestStreak > streak;
    return (
        <div
            data-testid="athlete-streak-strip"
            className="mb-6 flex flex-wrap items-center justify-between gap-4 border border-zinc-800 bg-gradient-to-r from-brand-navy-950/60 via-black to-brand-red/10 rounded-2xl px-5 py-4"
        >
            <div className="flex items-center gap-4 min-w-0">
                <div className="shrink-0 w-12 h-12 rounded-full bg-brand-red/15 border border-brand-red/40 flex items-center justify-center">
                    <Flame className="w-5 h-5 text-brand-red" />
                </div>
                <div className="min-w-0">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-300">Streak</div>
                    <div data-testid="athlete-streak-headline" className="text-sm text-white truncate">{headline}</div>
                    {showRecord && (
                        <div className="text-[10px] text-amber-400/80 mt-0.5" data-testid="athlete-streak-record">
                            Personal best: {longestStreak} weeks
                        </div>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-6 shrink-0">
                {daysStreak > 0 && (
                    <div className="text-right">
                        <div data-testid="athlete-streak-days" className="font-display text-2xl tracking-wide text-amber-300 leading-none">{daysStreak}</div>
                        <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-400 mt-1">day{daysStreak === 1 ? "" : "s"} in a row</div>
                    </div>
                )}
                <div className="text-right">
                    <div data-testid="athlete-streak-weeks" className="font-display text-3xl tracking-wide text-white leading-none">{streak}</div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mt-1">
                        wk · <span data-testid="athlete-streak-30d">{last30}</span> in 30d
                    </div>
                </div>
            </div>
        </div>
    );
}

function ActionCard({ testid, onClick, icon: Icon, title, sub, accent }) {
    const accentClass =
        accent === "navy"
            ? "border-brand-navy-700/60 hover:border-brand-navy-500 hover:bg-brand-navy-900/30"
            : "border-zinc-800 hover:border-brand-red hover:bg-brand-red/5";
    return (
        <button
            type="button"
            data-testid={testid}
            onClick={onClick}
            className={`group text-left bg-zinc-950/60 rounded-2xl p-6 border transition-colors ${accentClass}`}
        >
            <Icon className={`w-6 h-6 mb-4 ${accent === "navy" ? "text-brand-navy-300" : "text-brand-red"}`} />
            <div className="font-display text-2xl tracking-wider text-white">{title}</div>
            <div className="text-sm text-zinc-300 mt-1 leading-relaxed">{sub}</div>
            <div className="mt-4 text-[10px] tracking-[0.25em] uppercase text-zinc-400 inline-flex items-center gap-1.5 group-hover:text-white transition-colors">
                Open <ArrowRight className="w-3 h-3" />
            </div>
        </button>
    );
}

function SessionsCard({ sessionsRemaining, upcoming, canBook, onOpen }) {
    // ALWAYS show a real count — never default to "Unlimited" or "∞".
    // MaxSC subs come with a fixed pack of sessions; "unlimited" was always
    // a lie. Sachin 2026-02-28: "make sure it is the actual amount. Not
    // that infinity crap. Please make sure to never default to that again."
    const n = typeof sessionsRemaining === "number" ? Math.max(0, sessionsRemaining) : 0;
    return (
        <button
            type="button"
            data-testid="athlete-card-sessions"
            onClick={onOpen}
            className="group text-left bg-zinc-950/60 rounded-2xl p-6 border border-brand-navy-700/60 hover:border-brand-navy-500 hover:bg-brand-navy-900/30 transition-colors"
        >
            <Calendar className="w-6 h-6 mb-4 text-brand-navy-300" />
            <div className="font-display text-2xl tracking-wider text-white">My Sessions</div>
            <div className="mt-3 text-sm text-zinc-300">
                <span data-testid="athlete-sessions-remaining" className="text-2xl text-white font-display tracking-wide">
                    {n}
                </span>{" "}
                left
            </div>
            {upcoming.length > 0 ? (
                <ul className="mt-3 space-y-1 text-xs text-zinc-300">
                    {upcoming.slice(0, 3).map((b) => (
                        <li key={b.booking_id} data-testid={`athlete-upcoming-${b.booking_id}`}>
                            {formatDate(b.date)} · {b.start_time}
                        </li>
                    ))}
                    {upcoming.length > 3 && (
                        <li className="text-zinc-500">+{upcoming.length - 3} more</li>
                    )}
                </ul>
            ) : (
                <p className="mt-3 text-xs text-zinc-400">No sessions on the calendar yet.</p>
            )}
            <div className="mt-4 text-[10px] tracking-[0.25em] uppercase text-zinc-400 inline-flex items-center gap-1.5 group-hover:text-white transition-colors">
                {canBook ? "View & book" : "View schedule"} <ArrowRight className="w-3 h-3" />
            </div>
        </button>
    );
}

function MaxesCard({ maxes, onOpen }) {
    const entries = Object.values(maxes || {});
    return (
        <button
            type="button"
            data-testid="athlete-card-maxes"
            onClick={onOpen}
            className="group text-left bg-zinc-950/60 rounded-2xl p-6 border border-zinc-800 hover:border-brand-red hover:bg-brand-red/5 transition-colors"
        >
            <Activity className="w-6 h-6 mb-4 text-brand-red" />
            <div className="font-display text-2xl tracking-wider text-white">My Maxes</div>
            {entries.length === 0 ? (
                <p className="mt-3 text-xs text-zinc-300">
                    No maxes logged yet — they&apos;ll appear as you finish strength sets.
                </p>
            ) : (
                <ul className="mt-3 space-y-1 text-xs text-zinc-200">
                    {entries.slice(0, 4).map((m) => (
                        <li key={m.exercise_id} data-testid={`athlete-max-${m.exercise_id}`} className="flex justify-between gap-3">
                            <span className="truncate">{m.exercise_id}</span>
                            <span className="text-white">{m.value} lb</span>
                        </li>
                    ))}
                    {entries.length > 4 && (
                        <li className="text-zinc-500">+{entries.length - 4} more</li>
                    )}
                </ul>
            )}
            <div className="mt-4 text-[10px] tracking-[0.25em] uppercase text-zinc-400 inline-flex items-center gap-1.5 group-hover:text-white transition-colors">
                Open workouts <ArrowRight className="w-3 h-3" />
            </div>
        </button>
    );
}

const _DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function _fmtBookingDate(dateStr) {
    if (!dateStr) return "—";
    const [y, m, d] = dateStr.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    return `${_DAY_LABELS[dt.getDay()]}, ${dt.toLocaleString("en-US", { month: "short", day: "numeric" })}`;
}

function _fmtTime(t) {
    if (!t) return "";
    const [h, m] = t.split(":").map(Number);
    const period = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${String(m).padStart(2, "0")} ${period}`;
}

function AthleteSummaryStrip({ sessionsRemaining, upcoming, onScheduleClick }) {
    const next = (upcoming || [])[0];
    // ALWAYS show real count. Never ∞, never "Unlimited". Sachin 2026-02-28.
    const sessN = typeof sessionsRemaining === "number" ? Math.max(0, sessionsRemaining) : 0;
    return (
        <div data-testid="athlete-summary-strip" className="mb-8 grid sm:grid-cols-2 gap-3">
            <div
                data-testid="athlete-sessions-strip"
                className="border border-brand-navy-700/60 bg-brand-navy-950/30 rounded-2xl px-5 py-4"
            >
                <div className="text-[10px] tracking-[0.22em] uppercase text-brand-navy-200">Sessions remaining</div>
                <div className="font-display text-4xl tracking-wider text-white mt-1">{sessN}</div>
                <div className="text-[11px] text-zinc-400 mt-0.5">
                    {sessN > 0 ? "Across all active packs" : "No sessions left — talk to your coach"}
                </div>
            </div>
            <button
                type="button"
                onClick={onScheduleClick}
                data-testid="athlete-next-session-strip"
                className="border border-brand-red/40 bg-brand-red/10 rounded-2xl px-5 py-4 text-left hover:bg-brand-red/15 transition-colors"
            >
                <div className="text-[10px] tracking-[0.22em] uppercase text-brand-red">Next session</div>
                {next ? (
                    <>
                        <div className="font-display text-2xl tracking-wider text-white mt-1">
                            {_fmtTime(next.start_time)}
                        </div>
                        <div className="text-[11px] text-zinc-400 mt-0.5">
                            {_fmtBookingDate(next.date)} · {next.label || next.category || "Training"}
                        </div>
                    </>
                ) : (
                    <>
                        <div className="font-display text-2xl tracking-wider text-zinc-400 mt-1">—</div>
                        <div className="text-[11px] text-zinc-400 mt-0.5">Nothing on the calendar yet</div>
                    </>
                )}
            </button>
        </div>
    );
}

